import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Box, CircularProgress, LinearProgress, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { pdfImportProvider } from '../../providers/pdfImportProvider';
import type {
  PdfCurrentResponse,
  PdfJobStatusResponse,
  PdfStartJobRequest,
} from '../../types/pdfImport';
import { MyButton, MyDialog, SectionHeader } from '../uiComponents';
import { EditorSection } from './EditorSection';
import { pdfFilledButtonSx } from './pdfImportButtonStyles';
import { pdfImportErrorMessage, pdfJobErrorMessage } from './pdfImportErrors';

const MAX_PDF_BYTES = 25_000_000;
const POLL_INTERVAL_MS = 4_000;
const PENDING_START_KEY = 'pdf-import-pending-start';
const KNOWN_JOB_KEY = 'pdf-import-known-job';

type Phase =
  | 'loading'
  | 'idle'
  | 'intent'
  | 'uploading'
  | 'starting'
  | 'checking'
  | 'startUncertain';

const isActive = (job: PdfJobStatusResponse | null | undefined): boolean =>
  job?.status === 'queued' || job?.status === 'processing';

const readPendingStart = (): PdfStartJobRequest | null => {
  try {
    const value = sessionStorage.getItem(PENDING_START_KEY);
    if (!value) return null;
    const parsed: unknown = JSON.parse(value);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'uploadId' in parsed &&
      'idempotencyKey' in parsed &&
      typeof parsed.uploadId === 'string' &&
      typeof parsed.idempotencyKey === 'string'
    ) {
      return { uploadId: parsed.uploadId, idempotencyKey: parsed.idempotencyKey };
    }
  } catch {
    // Session storage is optional; the backend remains the source of truth.
  }
  return null;
};

const readKnownJobId = (): string | null => {
  try {
    return sessionStorage.getItem(KNOWN_JOB_KEY);
  } catch {
    return null;
  }
};

const validateFile = (file: File | null): string | null => {
  if (!file) return 'Please choose a PDF file.';
  if (file.type !== 'application/pdf' && !(file.type === '' && /\.pdf$/i.test(file.name)))
    return 'Please choose a PDF file.';
  if (file.size === 0) return 'The PDF is empty. Please choose another file.';
  if (file.size > MAX_PDF_BYTES) return 'The PDF must be 25 MB or smaller.';
  return null;
};

export const PdfImportEntry = () => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('loading');
  const [file, setFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [current, setCurrent] = useState<PdfCurrentResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [pendingStart, setPendingStart] = useState<PdfStartJobRequest | null>(readPendingStart);
  const mountedRef = useRef(false);
  const submitInFlightRef = useRef(false);
  const refreshInFlightRef = useRef(false);
  const latestJobRef = useRef<PdfJobStatusResponse | null>(null);
  const knownJobRef = useRef<string | null>(readKnownJobId());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const uploadControllerRef = useRef<AbortController | null>(null);

  const clearPendingStart = () => {
    try {
      sessionStorage.removeItem(PENDING_START_KEY);
    } catch {
      // Private browsing can block session storage.
    }
    setPendingStart(null);
  };

  const refreshCurrent = useCallback(async () => {
    if (refreshInFlightRef.current || !mountedRef.current) return;
    refreshInFlightRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);

    try {
      const response = await pdfImportProvider.getCurrent();
      if (!mountedRef.current) return;
      if (response.ok && response.data) {
        setCurrent(response.data);
        latestJobRef.current = response.data.job;
        if (!isActive(response.data.job)) {
          knownJobRef.current = null;
          try {
            sessionStorage.removeItem(KNOWN_JOB_KEY);
          } catch {
            // Server status is authoritative even when local storage is unavailable.
          }
        }
        setError(
          response.data.recoveryError
            ? pdfImportErrorMessage({ code: response.data.recoveryError })
            : null
        );
        setPhase((previous) =>
          previous === 'loading' || previous === 'checking'
            ? readPendingStart()
              ? 'startUncertain'
              : 'idle'
            : previous
        );
      } else {
        setError(pdfImportErrorMessage(response, 'Unable to check the current PDF analysis.'));
        setPhase((previous) =>
          previous === 'loading' || previous === 'checking'
            ? readPendingStart()
              ? 'startUncertain'
              : 'idle'
            : previous
        );
      }
    } catch {
      if (mountedRef.current) {
        setError('Unable to check the current PDF analysis. Please try again.');
        setPhase((previous) =>
          previous === 'loading' || previous === 'checking'
            ? readPendingStart()
              ? 'startUncertain'
              : 'idle'
            : previous
        );
      }
    } finally {
      refreshInFlightRef.current = false;
      if (mountedRef.current && isActive(latestJobRef.current)) {
        timerRef.current = setTimeout(() => void refreshCurrent(), POLL_INTERVAL_MS);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void refreshCurrent();
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
      uploadControllerRef.current?.abort();
    };
  }, [refreshCurrent]);

  const startUploadedJob = async (request: PdfStartJobRequest) => {
    setPhase('starting');
    try {
      const response = await pdfImportProvider.startJob(request);
      if (!mountedRef.current) return;
      if (response.ok && response.data?.jobId) {
        knownJobRef.current = response.data.jobId;
        try {
          sessionStorage.setItem(KNOWN_JOB_KEY, response.data.jobId);
        } catch {
          // In-memory tracking still prevents a second Start on this page.
        }
        clearPendingStart();
        setPhase('checking');
        await refreshCurrent();
        return;
      }
      if (response.status === 409 && response.code === 'pdf_job_active_conflict') {
        clearPendingStart();
        setPhase('idle');
        await refreshCurrent();
        setError(pdfImportErrorMessage(response));
        return;
      }
      setError(pdfImportErrorMessage(response));
      if (response.status && response.status >= 400 && response.status < 500) {
        clearPendingStart();
        setPhase('idle');
      } else {
        setPhase('startUncertain');
      }
    } catch {
      if (mountedRef.current) {
        setPhase('startUncertain');
        setError('The job response was lost. Check its status, then retry with the same request.');
      }
    }
  };

  const handleStart = async () => {
    if (
      submitInFlightRef.current ||
      pendingStart ||
      knownJobRef.current ||
      phase !== 'idle' ||
      isActive(current?.job)
    )
      return;
    const fileError = validateFile(file);
    if (fileError) {
      setError(fileError);
      return;
    }
    if (!file) return;
    submitInFlightRef.current = true;
    setError(null);
    setPhase('intent');
    try {
      const intent = await pdfImportProvider.createUploadIntent();
      if (!mountedRef.current) return;
      if (!intent.ok || !intent.data) {
        setError(pdfImportErrorMessage(intent));
        setPhase('idle');
        return;
      }
      if (file.size > intent.data.maxBytes) {
        setError('The PDF exceeds the upload limit. Please choose a smaller file.');
        setPhase('idle');
        return;
      }
      if (Date.parse(intent.data.postExpiresAt) <= Date.now()) {
        setError('The upload authorization expired. Please try again.');
        setPhase('idle');
        return;
      }
      const controller = new AbortController();
      uploadControllerRef.current = controller;
      setUploadProgress(null);
      setPhase('uploading');
      await pdfImportProvider.uploadToStorage(
        intent.data,
        file,
        controller.signal,
        (loaded, total) => {
          if (mountedRef.current && total && total > 0)
            setUploadProgress(Math.min(100, Math.round((loaded / total) * 100)));
        }
      );
      uploadControllerRef.current = null;
      if (!mountedRef.current) return;
      if (controller.signal.aborted) {
        setError('Upload cancelled. The file was not submitted for analysis.');
        setPhase('idle');
        return;
      }
      const randomBytes = crypto.getRandomValues(new Uint8Array(16));
      const request = {
        uploadId: intent.data.uploadId,
        idempotencyKey: Array.from(randomBytes, (byte) => byte.toString(16).padStart(2, '0')).join(
          ''
        ),
      };
      try {
        sessionStorage.setItem(PENDING_START_KEY, JSON.stringify(request));
      } catch {
        // The same key remains in memory for this browser session.
      }
      setPendingStart(request);
      await startUploadedJob(request);
    } catch (caught) {
      if (!mountedRef.current) return;
      if (axios.isCancel(caught)) {
        setError('Upload cancelled. The file was not submitted for analysis.');
      } else if (axios.isAxiosError(caught)) {
        setError(
          caught.response?.status === 403
            ? 'The upload authorization expired or storage rejected the file. Please try again.'
            : 'PDF upload failed. Check your connection and storage access, then try again.'
        );
      } else {
        setError('PDF upload failed. Please try again.');
      }
      setPhase('idle');
    } finally {
      uploadControllerRef.current = null;
      submitInFlightRef.current = false;
    }
  };

  const handleRetryStart = async () => {
    if (
      submitInFlightRef.current ||
      !pendingStart ||
      phase !== 'startUncertain' ||
      isActive(current?.job)
    )
      return;
    submitInFlightRef.current = true;
    setError(null);
    try {
      await startUploadedJob(pendingStart);
    } finally {
      submitInFlightRef.current = false;
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0] ?? null;
    event.target.value = '';
    setFile(selected);
    setUploadProgress(null);
    setError(validateFile(selected));
  };

  const handleCancelJob = async () => {
    if (!current?.job || !isActive(current.job) || cancelling) return;
    setCancelOpen(false);
    setCancelling(true);
    try {
      const response = await pdfImportProvider.cancelJob(current.job.jobId);
      if (!mountedRef.current) return;
      if (!response.ok) setError(pdfImportErrorMessage(response));
      await refreshCurrent();
      if (!response.ok) setError(pdfImportErrorMessage(response));
    } catch {
      if (mountedRef.current)
        setError('Unable to request cancellation. Please check the job status.');
    } finally {
      if (mountedRef.current) setCancelling(false);
    }
  };

  const busy =
    phase === 'intent' || phase === 'uploading' || phase === 'starting' || phase === 'checking';
  const job = current?.job;
  const preview = current?.preview;
  const previewExpired = preview ? Date.parse(preview.expiresAt) <= Date.now() : false;

  return (
    <Box component='section' data-testid='pdf-import-entry' minWidth={0} width='100%'>
      <SectionHeader title='Create a new listing' />
      <Stack paddingY={3} spacing={2.5}>
        <EditorSection
          title='Import from PDF'
          titleId='pdf-import-title'
          description='Upload one PDF (up to 25 MB and 20 pages). The page limit is checked during analysis.'
        >
          <Stack alignItems='flex-start' spacing={2}>
            <MyButton
              data={{
                buttonName: file ? 'Replace PDF' : 'Choose PDF',
                buttonType: 'button',
                variant: 'contained',
                customWidth: '170px',
                sx: pdfFilledButtonSx,
              }}
              disabled={busy || phase === 'loading' || phase === 'startUncertain'}
              onClick={() => document.getElementById('pdf-import-file')?.click()}
            />
            <input
              aria-label='PDF file'
              accept='application/pdf,.pdf'
              id='pdf-import-file'
              onChange={handleFileChange}
              style={{ display: 'none' }}
              type='file'
            />
            {file && (
              <Typography component='p'>
                {file.name} · {(file.size / 1_000_000).toFixed(2)} MB
              </Typography>
            )}
            {phase === 'intent' && <Typography role='status'>Preparing PDF upload...</Typography>}
            {phase === 'uploading' && (
              <Stack width='100%' spacing={1}>
                <Typography role='status'>
                  Uploading PDF{uploadProgress === null ? '...' : `: ${uploadProgress}%`}
                </Typography>
                {uploadProgress === null ? (
                  <LinearProgress />
                ) : (
                  <LinearProgress variant='determinate' value={uploadProgress} />
                )}
                <MyButton
                  data={{
                    buttonName: 'Cancel upload',
                    buttonType: 'button',
                    variant: 'contained',
                    customWidth: '170px',
                    sx: pdfFilledButtonSx,
                  }}
                  onClick={() => uploadControllerRef.current?.abort()}
                />
              </Stack>
            )}
            {(phase === 'starting' || phase === 'checking') && (
              <Typography role='status'>Starting PDF analysis...</Typography>
            )}
            {phase === 'startUncertain' && (
              <Typography role='alert'>
                The start response is uncertain. Check the current job before retrying with the same
                request.
              </Typography>
            )}
            {error && (
              <Typography color='error' role='alert'>
                {error}
              </Typography>
            )}
            {phase === 'loading' && <CircularProgress aria-label='Loading PDF import' size={24} />}
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
              <MyButton
                data={{
                  buttonName: 'Start analysis',
                  buttonType: 'button',
                  variant: 'contained',
                  customWidth: '170px',
                  sx: pdfFilledButtonSx,
                }}
                disabled={
                  phase !== 'idle' ||
                  Boolean(knownJobRef.current) ||
                  isActive(job) ||
                  validateFile(file) !== null
                }
                onClick={() => void handleStart()}
              />
              {phase === 'startUncertain' && pendingStart && (
                <MyButton
                  data={{
                    buttonName: 'Retry same start',
                    buttonType: 'button',
                    variant: 'contained',
                    customWidth: '170px',
                    sx: pdfFilledButtonSx,
                  }}
                  disabled={isActive(job)}
                  onClick={() => void handleRetryStart()}
                />
              )}
              <MyButton
                data={{
                  buttonName: 'Check status',
                  buttonType: 'button',
                  variant: 'contained',
                  customWidth: '170px',
                  sx: pdfFilledButtonSx,
                }}
                disabled={busy || phase === 'loading'}
                onClick={() => void refreshCurrent()}
              />
              <MyButton
                data={{
                  buttonName: 'Back to listings',
                  buttonType: 'button',
                  variant: 'contained',
                  customWidth: '170px',
                  sx: pdfFilledButtonSx,
                }}
                onClick={() => navigate('/ads/my')}
              />
            </Stack>
          </Stack>
        </EditorSection>

        {job && (
          <EditorSection title='Analysis status' titleId='pdf-job-status-title'>
            <Stack alignItems='flex-start' spacing={1.5}>
              <Typography>Status: {job.status}</Typography>
              {isActive(job) && (
                <Typography role='status'>
                  Analysis is running in the background. Confirmed checkpoints:{' '}
                  {job.confirmedCheckpoints}.
                </Typography>
              )}
              {job.cancelRequested && isActive(job) && (
                <Typography>Cancellation requested. Waiting for confirmation...</Typography>
              )}
              {job.status === 'completed' && (
                <Typography>Analysis completed. Preview metadata is available below.</Typography>
              )}
              {job.status === 'partial' && (
                <Typography>
                  Analysis completed partially. Open the available preview below and review the
                  missing information.
                </Typography>
              )}
              {job.status === 'failed' && (
                <Typography color='error' role='alert'>
                  {pdfJobErrorMessage(job)}
                </Typography>
              )}
              {job.status === 'cancelled' && <Typography>Analysis was cancelled.</Typography>}
              {isActive(job) && (
                <MyButton
                  data={{
                    buttonName: 'Cancel analysis',
                    buttonType: 'button',
                    variant: 'contained',
                    customWidth: '170px',
                    sx: pdfFilledButtonSx,
                  }}
                  disabled={cancelling || job.cancelRequested}
                  onClick={() => setCancelOpen(true)}
                />
              )}
            </Stack>
          </EditorSection>
        )}

        {preview && !previewExpired && (
          <EditorSection title='Available preview' titleId='pdf-preview-title'>
            <Stack alignItems='flex-start' spacing={2}>
              <Typography>
                Preview ready ({preview.processingStatus}). ID: {preview.previewId}
              </Typography>
              {preview.processingStatus === 'partial' && (
                <Typography role='alert'>
                  The analysis is partial. Review the missing information before continuing.
                </Typography>
              )}
              <MyButton
                data={{
                  buttonName: 'Open Preview',
                  buttonType: 'button',
                  variant: 'contained',
                  sx: pdfFilledButtonSx,
                }}
                onClick={() =>
                  navigate(`/ads/create/pdf/preview/${encodeURIComponent(preview.previewId)}`)
                }
              />
            </Stack>
          </EditorSection>
        )}
        {previewExpired && (
          <Typography role='alert'>The previous PDF preview has expired.</Typography>
        )}
        {!job && !preview && phase !== 'loading' && (
          <Typography>No PDF analysis has been started yet.</Typography>
        )}
      </Stack>

      <MyDialog
        open={cancelOpen}
        dialogTitle='Cancel PDF analysis?'
        width='480'
        onDismiss={() => setCancelOpen(false)}
        onClose={() => setCancelOpen(false)}
      >
        <Stack alignItems='center' spacing={2}>
          <Typography>
            The analysis may already be finishing. Cancellation is confirmed by the server.
          </Typography>
          <Stack direction='row' spacing={2}>
            <MyButton
              data={{
                buttonName: 'Keep analysis',
                buttonType: 'button',
                variant: 'contained',
                sx: pdfFilledButtonSx,
              }}
              onClick={() => setCancelOpen(false)}
            />
            <MyButton
              data={{
                buttonName: 'Cancel analysis',
                buttonType: 'button',
                variant: 'contained',
                sx: pdfFilledButtonSx,
              }}
              onClick={() => void handleCancelJob()}
            />
          </Stack>
        </Stack>
      </MyDialog>
    </Box>
  );
};
